import {
  IconSteeringWheel,
  IconMessageCircle,
  IconEdit,
  IconListCheck,
  IconPlugConnected,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { theme } from "../lib/theme";
import SEO from "@/components/SEO";
import SocialShare from "@/components/SocialShare";

const FONT = { fontFamily: "'Manrope', ui-sans-serif, system-ui, sans-serif" };

const {
  bg: BG,
  bgAlt: BG_ALT,
  dark: DARK,
  darkCard: DARK_CARD,
  darkBorder: DARK_BORDER,
  heading: HEADING,
  mutedText: MUTED_TEXT,
  red: RED,
  redLight: RED_LIGHT,
  accentBg: ACCENT_BG,
  border: BORDER,
  lightAccent: LIGHT_ACCENT,
} = theme;

const cardShadow = "shadow-[0_1px_2px_rgba(15,20,35,0.04),0_8px_24px_rgba(15,20,35,0.06)]";

type WorkflowStep = { title: string; description: string };
type PathStep = { title: string; description: string; icon: typeof IconEdit };
type ExperienceCard = {
  eyebrow: string;
  title: string;
  description: string;
  points: string[];
  symbol: string;
};

const workflowSteps: WorkflowStep[] = [
  {
    title: "Fewer irrelevant choices",
    description: "Only vehicles that fit the criteria you set.",
  },
  {
    title: "Side-by-side comparison",
    description: "History, condition, and pricing presented the same way for every option.",
  },
  {
    title: "You decide when to connect",
    description: "Contact details stay private until you choose to reach out.",
  },
];

const pillLabels = [
  "Buyer choice and control",
  "Side-by-side comparisons",
  "Relevant connections",
  "Coordinated delivery",
];

const pathSteps: PathStep[] = [
  {
    title: "Request",
    description: "Buyers define exactly what they need.",
    icon: IconEdit,
  },
  {
    title: "Source",
    description: "Matching vehicles become available to Buyers, Sellers, and Dealers.",
    icon: IconListCheck,
  },
  {
    title: "Transact",
    description: "Buyers and Sellers negotiate directly with full visibility.",
    icon: IconMessageCircle,
  },
  {
    title: "Deliver",
    description: "Coordinated transport completes the handoff.",
    icon: IconPlugConnected,
  },
];

const experienceCards: ExperienceCard[] = [
  {
    eyebrow: "Dealer Opportunity",
    title: "Create new profit opportunities across your inventory operation.",
    description:
      "Dealers reduce the cost and friction of traditional inventory channels. Acquire, move, and sell vehicles through a single, demand-driven pipeline.",
    points: [
      "Identify acquisition opportunities aligned with your current inventory needs.",
      "Expand your wholesale reach without auction fees.",
      "Improve the movement and utilization of your inventory.",
      "Cut the time and effort between sourcing and reselling a unit.",
      "Reduce your acquisition-to-sale operating costs.",
    ],
    symbol: "01",
  },
  {
    eyebrow: "Buyer Experience",
    title: "Make a more informed vehicle decision.",
    description:
      "Buyers receive vehicle options that fit their needs — no digging through irrelevant listings.",
    points: [
      "Receive vehicle responses that fit the request you've defined.",
      "Compare offers from multiple Sellers side by side.",
      "Message Sellers directly without sharing your number until you're ready.",
    ],
    symbol: "02",
  },
  {
    eyebrow: "Seller Experience",
    title: "Reach Buyers who already want your vehicle.",
    description:
      "Sellers respond to defined Buyer demand instead of waiting for Buyers to find their listings.",
    points: [
      "See Buyer requests that genuinely fit your vehicle.",
      "Present your vehicle once and move on.",
      "Compare Dealer offers.",
      "Respond efficiently to relevant Buyer interest.",
    ],
    symbol: "03",
  },
  {
    eyebrow: "Transporter Experience",
    title: "Make every route more productive.",
    description:
      "Transporters can find work that fits their location, availability, and operating preferences.",
    points: [
      "Put available capacity to better use.",
      "Reduce empty miles.",
      "Choose the routes and opportunities that fit your operation.",
      "Find opportunities across a nationwide network.",
      "Work directly with Dealers without unnecessary intermediaries.",
    ],
    symbol: "04",
  },
];

export default function Home() {
  return (
    <main style={{ ...FONT, background: BG, color: HEADING }} className="antialiased">
      <SEO
        title="Dealer Workflow Software for Automotive | Motosport Software"
        description="Motosport Software connects buyer intent, dealer inventory, and transaction workflows into a single system for acquiring, managing, transporting, and selling vehicles."
        url="https://motosportsoftware.com/"
      />
      <HeroSection />
      <PillSection />
      <RealRequestsBand />
      <TrustSection />
      <InventoryStartSection />
      <ExperienceSection />
      <DarkPathSection />
      <TransportCta />
    </main>
  );
}

function HeroSection() {
  return (
    <section style={{ background: BG }} className="px-4 pb-6 pt-4 sm:px-6">
      <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[1.05fr_1fr] lg:gap-12">
        <div className="flex flex-col justify-center gap-[18px]">
          <div
            className="inline-flex w-fit items-center gap-2 rounded-full border px-3.5 py-2.5 text-[13px] sm:text-[16px] font-medium italic"
            style={{ background: ACCENT_BG, color: RED, borderColor: RED }}
          >
            <IconSteeringWheel size={16} stroke={2} />
            Coming soon—a new way to buy, sell, and move vehicles
          </div>

          <h1
            className="max-w-[620px] text-[38px] font-bold leading-[1.08] tracking-tight sm:text-[48px] lg:text-[48px]"
            style={{ color: HEADING, letterSpacing: "-1.4px" }}
          >
            Finding the vehicle you want without{" "}
            <span style={{ color: RED }}>searching through the noise</span>
          </h1>
          <p
            className="max-w-[590px] border-l-[3px] pl-3.5 text-[17px] font-bold leading-[26px]"
            style={{ color: HEADING, borderColor: RED }}
          >
            Motosport Software develops technology that simplifies vehicle acquisition, management,
            transportation, and sales—helping Buyers, Sellers, Dealers, and Transporters work
            together more efficiently.
          </p>

          <SocialShare
            title="Dealer Workflow Software for Automotive | Motosport Software"
            url="https://motosportsoftware.com/"
          />
        </div>

        <div className="rounded-[24px] p-6 sm:p-7" style={{ background: ACCENT_BG }}>
          <p className="text-[18px] font-medium italic leading-[30px]" style={{ color: HEADING }}>
            What matters to you?
          </p>
          <p className="text-[18px] font-medium italic leading-[30px]" style={{ color: RED }}>
            "I want a reliable three-row SUV with 4WD, under $20,000, that can tow 5,000 lb."
          </p>
          <p className="mt-1 text-right text-[13px]" style={{ color: MUTED_TEXT }}>
            ♡ Sample Buyer Request
          </p>

          <div className="mt-4 space-y-2.5">
            {workflowSteps.map((step) => (
              <div key={step.title} className={`flex gap-3 rounded-2xl bg-white p-4 ${cardShadow}`}>
                <span className="mt-2 h-2 w-2 shrink-0 rounded-full" style={{ background: RED }} />
                <div>
                  <p className="text-[17px] font-extrabold" style={{ color: HEADING }}>
                    {step.title}
                  </p>
                  <p className="mt-1 text-[14px] leading-[21px]" style={{ color: MUTED_TEXT }}>
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function PillSection() {
  return (
    <section style={{ background: BG }} className="px-4 py-4 sm:px-6">
      <div className="mx-auto flex max-w-6xl flex-wrap justify-center gap-2 sm:gap-3">
        {pillLabels.map((pill) => (
          <span
            key={pill}
            className="inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-xs font-extrabold uppercase tracking-wide"
            style={{ background: ACCENT_BG, borderColor: RED, color: RED, letterSpacing: "1px" }}
          >
            <span className="h-[7px] w-[7px] rounded-full" style={{ background: RED }} />
            {pill}
          </span>
        ))}
      </div>
    </section>
  );
}

function RealRequestsBand() {
  return (
    <section
      className="relative flex min-h-[300px] w-full items-center overflow-hidden sm:min-h-[330px]"
      style={{ isolation: "isolate" }}
    >
      <img
        src="/images/MotosportSUV.webp"
        alt="Vehicle representing real Buyer demand"
        width={1900}
        height={700}
        loading="lazy"
        decoding="async"
        className="absolute inset-0 block h-full w-full object-cover"
        style={{ objectPosition: "center 45%" }}
      />

      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, rgba(8,14,22,0.76) 0%, rgba(20,24,30,0.58) 38%, rgba(72,39,31,0.32) 68%, rgba(42,24,22,0.18) 100%)",
        }}
      />

      <div className="relative z-10 mx-auto w-full max-w-6xl px-6 py-12 sm:px-8 lg:px-6">
        <div className="mx-auto max-w-[650px] text-center sm:mx-0 sm:text-left">
          <p
            className="text-xs font-extrabold uppercase tracking-wide"
            style={{ color: LIGHT_ACCENT, letterSpacing: "1.2px", textShadow: "0 1px 3px #000" }}
          >
            REAL BUYER DEMAND
          </p>

          <h2
            className="mt-2 text-[30px] font-extrabold leading-[36px] text-white sm:text-[38px] sm:leading-[44px]"
            style={{ letterSpacing: "-0.8px", textShadow: "0 2px 5px #000" }}
          >
            One request. <span style={{ color: LIGHT_ACCENT }}>Better vehicle opportunities.</span>
          </h2>

          <p
            className="mt-4 max-w-[620px] text-[16px] font-semibold leading-[25px] text-white"
            style={{ textShadow: "0 1px 3px #000" }}
          >
            Defined Buyer requests create relevant opportunities for Sellers and Dealers, with a
            clear path to direct communication.
          </p>
        </div>
      </div>
    </section>
  );
}

function TrustSection() {
  const cards = [
    {
      title: "Buyer contact protection",
      body: "Contact details stay private unless the Buyer initiates the conversation.",
    },
    {
      title: "Standardized vehicle information",
      body: "Responses follow one shared structure so you can evaluate options quickly and fairly.",
    },
    {
      title: "Shared accountability",
      body: "Clear identities and direct communication help maintain accountability from the first message through the final handoff.",
    },
  ];

  return (
    <section style={{ background: DARK }} className="px-4 py-12 sm:px-6 sm:py-14">
      <div className="mx-auto max-w-6xl">
        <p
          className="text-xs font-extrabold uppercase tracking-wide"
          style={{ color: LIGHT_ACCENT, letterSpacing: "1.2px" }}
        >
          THE MOTOSPORT TRANSPARENCY STANDARD
        </p>
        <h2
          className="mt-3 max-w-[880px] text-[31px] font-extrabold leading-[37px] text-white sm:text-[42px] sm:leading-[48px]"
          style={{ letterSpacing: "-0.8px" }}
        >
          Buyer-led and transparent by design, with accountability built in for every participant.
        </h2>
        <p className="mt-4 max-w-[760px] text-[16px] leading-[25px]" style={{ color: "#D7DCE5" }}>
          Every stage is designed to provide the information participants need, promote
          accountability, and keep the Buyer in control of the pace.
        </p>
        <div className="mt-8 grid gap-3.5 lg:grid-cols-3">
          {cards.map((card) => (
            <div
              key={card.title}
              className="rounded-[18px] p-5"
              style={{ background: DARK_CARD, border: `1px solid ${DARK_BORDER}` }}
            >
              <h3 className="text-[17px] font-extrabold leading-[23px] text-white">{card.title}</h3>
              <p className="mt-2 text-[14px] leading-[22px]" style={{ color: "#C6CDD8" }}>
                {card.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function InventoryStartSection() {
  return (
    <section style={{ background: BG_ALT }} className="px-4 py-8 sm:px-6">
      <div className="mx-auto max-w-6xl">
        <h2
          className="max-w-[780px] text-[32px] font-extrabold leading-[36px] sm:text-[42px] sm:leading-[46px]"
          style={{ color: HEADING, letterSpacing: "-0.8px" }}
        >
          Most vehicle sites start with inventory.{" "}
          <span style={{ color: RED }}>We start with your needs.</span>
        </h2>
        <p className="mt-4 max-w-[680px] text-[16px] leading-[25px]" style={{ color: MUTED_TEXT }}>
          Buyer requests and Dealer acquisition needs create defined opportunities for Sellers,
          Dealers, and Transporters.
        </p>
      </div>
    </section>
  );
}

function ExperienceSection() {
  return (
    <section style={{ background: BG }} className="px-4 pb-14 sm:px-6">
      <div className="mx-auto max-w-6xl">
        <div className="mt-8 flex flex-wrap gap-4">
          {experienceCards.map((card) => {
            const isFeatured = card.symbol === "01";
            return (
              <div
                key={card.symbol}
                className={`flex flex-col rounded-[20px] p-6 ${
                  isFeatured
                    ? "w-full border-2"
                    : "flex-1 basis-full sm:basis-[48%] min-w-0 sm:min-w-[280px] border"
                } ${cardShadow}`}
                style={{
                  background: isFeatured ? DARK : "#FFFFFF",
                  borderColor: isFeatured ? RED : BORDER,
                }}
              >
                <div className="mb-4 flex items-center">
                  <span
                    className="inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-xs font-extrabold uppercase tracking-wide"
                    style={{
                      background: ACCENT_BG,
                      borderColor: RED,
                      color: RED,
                      letterSpacing: "1px",
                    }}
                  >
                    <span className="h-[7px] w-[7px] rounded-full" style={{ background: RED }} />
                    {card.eyebrow}
                  </span>
                </div>

                <h3
                  className={`font-extrabold tracking-tight ${
                    isFeatured
                      ? "text-[28px] leading-[35px] text-white"
                      : "text-[22px] leading-[28px]"
                  }`}
                  style={{ color: isFeatured ? undefined : HEADING, letterSpacing: "-0.35px" }}
                >
                  {card.title}
                </h3>
                <p
                  className="mt-2.5 text-[15px] leading-[23px]"
                  style={{ color: isFeatured ? "#D7DCE5" : MUTED_TEXT }}
                >
                  {card.description}
                </p>

                <ul className="mt-4 flex-1 space-y-2.5">
                  {card.points.map((point, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <span
                        className="mt-[7px] h-[7px] w-[7px] shrink-0 rounded-full"
                        style={{ background: RED }}
                      />
                      <span
                        className="text-[14px] leading-[21px]"
                        style={{ color: isFeatured ? "#FFFFFF" : HEADING }}
                      >
                        {point}
                      </span>
                    </li>
                  ))}
                </ul>

                {card.symbol === "01" && (
                  <>
                    <Link
                      to="/dealer-code-of-conduct"
                      className="mt-4 inline-flex min-h-[44px] items-center self-start text-[13px] font-bold text-white underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                    >
                      View Dealer Code of Conduct
                    </Link>
                    <Link
                      to="/request-demo"
                      className="mt-3 inline-flex min-h-[48px] w-fit shrink-0 items-center justify-center rounded-full px-7 py-3 text-center text-sm font-bold text-white transition hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                      style={{ background: RED }}
                    >
                      Dealer Demo
                    </Link>
                  </>
                )}

                {card.symbol === "04" && (
                  <Link
                    to="/request-demo"
                    className="mt-4 inline-flex min-h-[44px] items-center self-start rounded-full px-6 py-3 text-sm font-bold text-white transition hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                    style={{ background: RED }}
                  >
                    Transporter Demo
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function DarkPathSection() {
  return (
    <section
      id="how-it-works"
      style={{ background: DARK }}
      className="scroll-mt-24 px-4 py-9 sm:px-6"
    >
      <div className="mx-auto max-w-6xl">
        <h2
          className="text-[34px] font-extrabold leading-[42px] text-white sm:text-[42px] sm:leading-[46px]"
          style={{ letterSpacing: "-1.1px" }}
        >
          One path from request through delivery.
        </h2>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {pathSteps.map(({ title, description, icon: Icon }) => (
            <div
              key={title}
              className="flex min-h-[160px] flex-col gap-3 rounded-[20px] p-[22px]"
              style={{ background: DARK_CARD, border: `1px solid ${DARK_BORDER}` }}
            >
              <Icon size={32} stroke={1.5} color={RED_LIGHT} />
              <h3 className="text-[17px] font-bold leading-[24px] text-white">{title}</h3>
              <p className="text-[14px] leading-[23px]" style={{ color: "#C6CDD8" }}>
                {description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TransportCta() {
  return (
    <section
      className="relative flex min-h-[250px] w-full items-center justify-center overflow-hidden"
      style={{ isolation: "isolate" }}
    >
      <img
        src="/images/Transporting.webp"
        alt="Vehicle transportation coordination"
        width={1900}
        height={700}
        loading="lazy"
        decoding="async"
        className="absolute inset-0 block h-full w-full object-cover"
        style={{ objectPosition: "center 40%" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(to bottom right, rgba(10,17,26,0.55) 0%, rgba(90,44,38,0.55) 100%)",
        }}
      />
      <div className="relative z-10 mx-auto max-w-[768px] px-6 py-12 text-center">
        <h2
          className="text-[28px] font-extrabold leading-[36px] text-white sm:text-[34px] sm:leading-[40px] lg:text-[42px] lg:leading-[46px]"
          style={{ letterSpacing: "-1px", textShadow: "0 2px 5px #000" }}
        >
          Transportation that{" "}
          <span style={{ color: LIGHT_ACCENT }}>stays connected to the transaction.</span>
        </h2>
        <p
          className="mx-auto mt-4 max-w-[620px] text-[16px] font-semibold leading-[25px] text-white"
          style={{ textShadow: "0 1px 3px #000" }}
        >
          See how Motosport Software keeps vehicles moving wherever the deal takes them.
        </p>
        <Link
          to="/request-demo"
          className="mt-6 inline-flex min-h-[48px] items-center justify-center rounded-full px-7 py-3 text-sm font-bold text-white transition hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          style={{ background: RED }}
        >
          Request a Demo
        </Link>
      </div>
    </section>
  );
}
